/*
 * File Path: client/src/components/dashboard/devices/EditDeviceModal.js
 * Description: Modal component for editing device details (name, location, property).
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * BDAO-SIS Project
 * Version: 1.1.3 // Fixed pre-fill logic to use device_name
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-05-02 // Fixed pre-fill logic
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaSave } from 'react-icons/fa';
import LoadingSpinner from '../../common/LoadingSpinner';
import { updateDevice } from '../../../api/authApi';
import './EditDeviceModal.css';

function EditDeviceModal({
    isOpen,
    onClose,
    onUpdateSuccess,
    device, // The device object being edited
    availableProperties
}) {
    // console.log('[EditDeviceModal] Received props:', { isOpen, device, availableProperties }); // Debug log

    const { t } = useTranslation();
    const [deviceName, setDeviceName] = useState('');
    const [deviceLocation, setDeviceLocation] = useState('');
    const [selectedPropertyId, setSelectedPropertyId] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Populate form when device data changes (modal opens)
    useEffect(() => {
        // console.log('[EditDeviceModal] useEffect running. isOpen:', isOpen, 'Device:', device);
        if (isOpen && device) {
            // --- CORRECTED: Use device.device_name ---
            const nameToSet = device.device_name || ''; // Use device_name from fetched data
            // --- End Correction ---
            const locationToSet = device.location || '';
            const propertyToSet = device.property_id || '';
            // console.log(`[EditDeviceModal] Setting state - Name: "${nameToSet}", Location: "${locationToSet}", Property: "${propertyToSet}"`);
            setDeviceName(nameToSet);
            setDeviceLocation(locationToSet);
            setSelectedPropertyId(propertyToSet);
            setError('');
        }
    }, [isOpen, device]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setError('');
        if (name === 'deviceName') setDeviceName(value);
        else if (name === 'deviceLocation') setDeviceLocation(value);
        else if (name === 'propertyId') setSelectedPropertyId(value);
    };

    const handleSave = async () => {
        setError('');
        if (!deviceName.trim()) { setError(t('devices.error.nameRequired', 'Device name is required.')); return; }
        if (deviceName.length > 12) { setError(t('devices.error.nameTooLong', 'Device name cannot exceed 12 characters.')); return; }
        if (deviceLocation && deviceLocation.length > 12) { setError(t('devices.error.locationTooLong', 'Location cannot exceed 12 characters.')); return; }
        if (!selectedPropertyId) { setError(t('devices.error.propertyRequired', 'Please select a property.')); return; }

        setIsLoading(true);
        try {
            // --- CORRECTED: Send 'name' in payload, matching backend expectation ---
            const payload = {
                name: deviceName.trim(), // Send as 'name'
                location: deviceLocation.trim() || null,
                property_id: parseInt(selectedPropertyId, 10)
            };
             // --- End Correction ---
            console.log(`[EditDeviceModal] Updating device ${device.device_id} with payload:`, payload);
            await updateDevice(device.device_id, payload);
            onUpdateSuccess();
        } catch (err) {
            setError(err.message || t('devices.error.updateFailed', 'Failed to update device.'));
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen || !device) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content edit-device-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose} title={t('common.close', 'Close')}> <FaTimes /> </button>
                <h2>{t('devices.editModalTitle', 'Edit Device')}</h2>
                {error && <div className="form-error modal-error">{error}</div>}
                <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                    <div className="form-group">
                        <label htmlFor="deviceName">{t('devices.deviceNameLabel', 'Device Name (max 12 chars):')}</label>
                        <input type="text" id="deviceName" name="deviceName" value={deviceName} onChange={handleInputChange} maxLength="12" required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="deviceLocation">{t('devices.deviceLocationLabel', 'Location (max 12 chars):')}</label>
                        <input type="text" id="deviceLocation" name="deviceLocation" value={deviceLocation} onChange={handleInputChange} maxLength="12" />
                    </div>
                    <div className="form-group">
                        <label htmlFor="propertyId">{t('devices.devicePropertyLabel', 'Property:')}</label>
                        <select id="propertyId" name="propertyId" value={selectedPropertyId} onChange={handleInputChange} required >
                            <option value="" disabled>{t('devices.selectProperty', 'Select Property')}</option>
                            {/* {console.log('[EditDeviceModal] Mapping availableProperties:', availableProperties)} */}
                            {availableProperties && availableProperties.map(prop => {
                                // {console.log('[EditDeviceModal] Rendering option for property:', prop);}
                                return ( <option key={prop.property_id} value={prop.property_id}> {prop.property_name} </option> );
                            })}
                        </select>
                    </div>
                    <div className="modal-actions">
                        <button type="submit" className="form-action-button save-button" disabled={isLoading} >
                            {isLoading ? <LoadingSpinner size="extrasmall" colorOverride="#fff" /> : <FaSave />}
                            {t('common.save', 'Save Changes')}
                        </button>
                        <button onClick={onClose} type="button" className="form-action-button cancel-button" disabled={isLoading} >
                            {t('common.cancel', 'Cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditDeviceModal;
